import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconPlus,
  IconTrash,
  IconWand,
} from '@tabler/icons-react';
import type { OrderRouteSegmentInput, TransportMode } from '@expeditor/shared';
import { buildDefaultRouteSegments, validateRouteSegments } from '@expeditor/shared';
import { referenceDataApi } from '../../shared/api/reference-data';
import { CodeSelect } from '../../shared/ui/CodeSelect';
import { DateField } from '../../shared/ui/DateField';
import { ROUTE_POINT_KIND_LABEL, TRANSPORT_MODE_LABEL } from '../../shared/ui/order-labels';

const MODE_OPTIONS = ['AUTO', 'RAIL', 'SEA', 'INLAND_WATER', 'AIR'].map((value) => ({
  value,
  label: TRANSPORT_MODE_LABEL[value] ?? value,
}));
const POINT_KIND_OPTIONS = Object.entries(ROUTE_POINT_KIND_LABEL).map(([value, label]) => ({
  value,
  label,
}));

/** ISO-datetime ↔ `YYYY-MM-DD` для DateField. */
const toDateInput = (v?: string | null) => (v ? v.slice(0, 10) : '');
const fromDateInput = (v: string) => (v ? new Date(`${v}T00:00:00.000Z`).toISOString() : undefined);

interface Props {
  value: OrderRouteSegmentInput[];
  onChange: (segments: OrderRouteSegmentInput[]) => void;
  /** Выбранные в поручении виды транспорта — из них строится типовая раскладка. */
  modes: TransportMode[];
  /** Концы маршрута из точек погрузки/выгрузки поручения. */
  ends: {
    pickupName?: string | null;
    pickupCountry?: string | null;
    dropoffName?: string | null;
    dropoffCountry?: string | null;
  };
}

/**
 * Редактор плеч маршрута: участок = один вид транспорта между двумя пунктами.
 *
 * Заполняется опционально. Кнопка «Сформировать по видам транспорта» даёт
 * типовую раскладку (авто → жд → авто), узлы (станции/порты) — свободный текст.
 */
export function RouteSegmentsEditor({ value, onChange, modes, ends }: Props) {
  const problems = validateRouteSegments(value);

  const update = (index: number, patch: Partial<OrderRouteSegmentInput>) => {
    onChange(value.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  /** Пересчёт порядковых номеров после добавления/удаления/перестановки. */
  const renumber = (list: OrderRouteSegmentInput[]) =>
    list.map((s, i) => ({ ...s, sequence: i + 1 }));

  const addSegment = () => {
    const prev = value[value.length - 1];
    onChange(
      renumber([
        ...value,
        {
          sequence: value.length + 1,
          transportMode: 'AUTO',
          // Новое плечо стартует там, где закончилось предыдущее.
          fromKind: prev ? prev.toKind : 'SHIPPER',
          fromName: prev ? prev.toName : (ends.pickupName ?? ''),
          fromCountry: prev ? prev.toCountry : (ends.pickupCountry ?? undefined),
          fromCountryCode: prev ? prev.toCountryCode : undefined,
          toKind: 'CONSIGNEE',
          toName: ends.dropoffName ?? '',
          toCountry: ends.dropoffCountry ?? undefined,
        },
      ]),
    );
  };

  const removeSegment = (index: number) => {
    onChange(renumber(value.filter((_, i) => i !== index)));
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const list = [...value];
    [list[index], list[target]] = [list[target], list[index]];
    onChange(renumber(list));
  };

  const generate = () => {
    if (value.length > 0 && !confirm('Заменить текущие плечи типовой раскладкой?')) return;
    onChange(buildDefaultRouteSegments(modes, ends));
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Плечи маршрута: участок пути под одним видом транспорта. Станции, порты и терминалы
          указываются свободным текстом.
        </Text>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconWand size={14} />}
            onClick={generate}
            disabled={modes.length === 0}
          >
            Сформировать по видам транспорта
          </Button>
          <Button size="xs" variant="default" leftSection={<IconPlus size={14} />} onClick={addSegment}>
            Добавить плечо
          </Button>
        </Group>
      </Group>

      {value.length === 0 && (
        <Text size="sm" c="dimmed">
          Плечи не заданы — маршрут учитывается как «от отправителя до получателя».
        </Text>
      )}

      {value.map((s, i) => (
        <Card key={s.id ?? i} withBorder padding="sm">
          <Group justify="space-between" mb="xs">
            <Text fw={500} size="sm">
              Плечо {i + 1}
            </Text>
            <Group gap={4}>
              <ActionIcon variant="subtle" onClick={() => move(i, -1)} disabled={i === 0}>
                <IconArrowUp size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                onClick={() => move(i, 1)}
                disabled={i === value.length - 1}
              >
                <IconArrowDown size={16} />
              </ActionIcon>
              <ActionIcon variant="subtle" color="red" onClick={() => removeSegment(i)}>
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </Group>

          <Stack gap="xs">
            <Group grow align="flex-start">
              <Select
                label="Вид транспорта"
                data={MODE_OPTIONS}
                value={s.transportMode}
                onChange={(v) => v && update(i, { transportMode: v as TransportMode })}
              />
              <TextInput
                label="Перевозчик (необязательно)"
                value={s.carrierName ?? ''}
                onChange={(e) => update(i, { carrierName: e.currentTarget.value || undefined })}
              />
            </Group>

            <Group grow align="flex-start">
              <Select
                label="Откуда — тип пункта"
                data={POINT_KIND_OPTIONS}
                value={s.fromKind}
                onChange={(v) => v && update(i, { fromKind: v as OrderRouteSegmentInput['fromKind'] })}
              />
              <TextInput
                label="Откуда — наименование"
                placeholder="адрес, станция, порт"
                value={s.fromName}
                onChange={(e) => update(i, { fromName: e.currentTarget.value })}
              />
              <CodeSelect
                fetchOptions={(q) => referenceDataApi.classifierSearch('country', q)}
                fetchByCode={(c) => referenceDataApi.classifierByCode('country', c)}
                label="Откуда — страна"
                value={s.fromCountryCode ?? ''}
                // Код и название страны ставим одним обновлением: два подряд
                // затёрли бы друг друга (оба читают value из замыкания).
                onChange={(code) => {
                  if (!code) update(i, { fromCountryCode: undefined, fromCountry: undefined });
                }}
                onOptionPick={(row) =>
                  row && update(i, { fromCountryCode: row.code, fromCountry: row.name })
                }
              />
            </Group>

            <Group grow align="flex-start">
              <Select
                label="Куда — тип пункта"
                data={POINT_KIND_OPTIONS}
                value={s.toKind}
                onChange={(v) => v && update(i, { toKind: v as OrderRouteSegmentInput['toKind'] })}
              />
              <TextInput
                label="Куда — наименование"
                placeholder="адрес, станция, порт"
                value={s.toName}
                onChange={(e) => update(i, { toName: e.currentTarget.value })}
              />
              <CodeSelect
                fetchOptions={(q) => referenceDataApi.classifierSearch('country', q)}
                fetchByCode={(c) => referenceDataApi.classifierByCode('country', c)}
                label="Куда — страна"
                value={s.toCountryCode ?? ''}
                onChange={(code) => {
                  if (!code) update(i, { toCountryCode: undefined, toCountry: undefined });
                }}
                onOptionPick={(row) =>
                  row && update(i, { toCountryCode: row.code, toCountry: row.name })
                }
              />
            </Group>

            <Group grow align="flex-start">
              <DateField
                label="Дата с"
                value={toDateInput(s.dateFrom)}
                onChange={(iso) => update(i, { dateFrom: fromDateInput(iso) })}
              />
              <DateField
                label="Дата по"
                value={toDateInput(s.dateTo)}
                onChange={(iso) => update(i, { dateTo: fromDateInput(iso) })}
              />
              <TextInput
                label="Комментарий"
                value={s.comment ?? ''}
                onChange={(e) => update(i, { comment: e.currentTarget.value || undefined })}
              />
            </Group>
          </Stack>
        </Card>
      ))}

      {problems.length > 0 && (
        <Alert color="yellow" title="Проверьте плечи маршрута">
          <Stack gap={2}>
            {problems.map((p) => (
              <Text key={p} size="sm">
                • {p}
              </Text>
            ))}
          </Stack>
          <Text size="xs" c="dimmed" mt={4}>
            Замечания не мешают сохранить черновик — маршрут можно уточнить позже.
          </Text>
        </Alert>
      )}
    </Stack>
  );
}
